/**
 * Choosing a photo for a dish.
 *
 * The picture is shrunk in the browser and sent to the server the moment it is
 * chosen, so by the time the dish is saved its image already has an address
 * and the save is the same one-line write it always was. Sending it early also
 * means the person sees the real, resized picture — the one the till will show
 * — before they commit, not a hopeful preview.
 *
 * A photo already on disk is left alone until a new one replaces it; "Remove"
 * only clears the link this dish holds, it does not chase the file.
 */
import { useRef, useState } from 'react';

import { api } from '../../lib/api.js';
import { shrinkImage } from '../../lib/shrinkImage.js';

export function ImagePicker({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function pick(file) {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const shrunk = await shrinkImage(file);
      const form = new FormData();
      form.append('image', shrunk, 'dish.jpg');
      const { url } = await api.upload('/menu/uploads', form);
      onChange(url);
    } catch (err) {
      setError(err?.message ?? 'That photo could not be added.');
    } finally {
      setBusy(false);
      // Let the same file be chosen again after a failure.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div>
      <span className="mb-1.5 block text-xs text-ink-3">Photo</span>

      <div className="flex items-center gap-3">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-rule bg-surface-3">
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-2xl text-ink-3">
              🍽
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="rounded-lg border border-rule px-3 py-1.5 text-sm text-ink-2 hover:text-ink disabled:opacity-60"
          >
            {busy ? 'Adding…' : value ? 'Change photo' : 'Add a photo'}
          </button>
          {value && !busy && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-left text-xs text-ink-3 underline hover:text-bad"
            >
              Remove
            </button>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </div>

      {error && <p className="mt-1.5 text-xs text-bad">{error}</p>}
    </div>
  );
}
